import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EndpointService } from 'src/app/services/endpoint.service';
import { LabImportColumnMap, LabImportCommitPayload, LabImportOrientationOverride, LabImportProposal, LabResultImportResponse } from './lab-result-import.model';

// DASH-2722: filename the FE stamps on the downloaded empty results template. A blob download can't read the
// BE's Content-Disposition, so the caller owns the name. The BE uses a different scheme
// (lab-result-template-{sanitised}.csv, with a req-{id} fallback for an empty number); this FE name is what the
// user actually sees for this blob consumer.
export function labResultTemplateFileName(labRequisitionNumber: string): string {
  return `${labRequisitionNumber}_Results_Template.csv`;
}

@Injectable({ providedIn: 'root' })
export class LabResultImportService extends EndpointService {
  constructor(http: HttpClient) {
    super(http);
  }

  // POST api/lab-result-imports/analyze (multipart) -> LabImportProposal. Ingests nothing.
  // `orientation` (DASH-2694) lets the user re-run detection with an explicit orientation override; only the
  // fields the user changed are appended, so a BE predating the paired change simply ignores them.
  // `columnMap` (DASH-3159) carries the user's confirmed column map on a RE-ANALYZE so the BE re-derives the
  // analyte/unit previews from the corrected columns (e.g. a fixed Sample Number column) instead of
  // re-detecting them. Only appended when supplied — the initial analyze has no user map yet, and a BE
  // predating the paired change ignores the extra `columns` part. Serialized identically to the commit part.
  analyze(
    labRequisitionId: string,
    file: File,
    orientation?: LabImportOrientationOverride,
    columnMap?: LabImportColumnMap,
  ): Observable<LabImportProposal> {
    const formData = new FormData();
    formData.append('labRequisitionId', labRequisitionId.toString());
    formData.append('file', file);
    if (orientation?.transposed != null) formData.append('transposed', orientation.transposed.toString());
    if (orientation?.dataStartRowIndex != null) formData.append('dataStartRowIndex', orientation.dataStartRowIndex.toString());
    if (columnMap) formData.append('columns', JSON.stringify(columnMap));
    return this.http.post<LabImportProposal>(this.getApiUrl('lab-result-imports/analyze'), formData);
  }

  // GET api/lab-result-imports/template/{labRequisitionId} -> empty lab-result CSV template (DASH-2722).
  // Regenerated on demand from the current requisition data; never persisted. Caller names the file via
  // labResultTemplateFileName() and hands the blob to downloadBlob().
  downloadTemplate(labRequisitionId: string): Observable<Blob> {
    return this.http.get(this.getApiUrl(`lab-result-imports/template/${labRequisitionId}`), { responseType: 'blob' });
  }

  // POST api/lab-result-imports/commit (multipart) -> LabResultImportResponse.
  // Status is meaningful: 200 clean, 207 partial, 400 nothing imported — callers must read the body in every case.
  commit(payload: LabImportCommitPayload): Observable<LabResultImportResponse> {
    const formData = new FormData();
    formData.append('labRequisitionId', payload.labRequisitionId.toString());
    formData.append('file', payload.file);
    formData.append('applyBlankCorrection', payload.applyBlankCorrection.toString());
    formData.append('columns', JSON.stringify(payload.columns));
    formData.append('analytes', JSON.stringify(payload.analytes));
    formData.append('units', JSON.stringify(payload.units));
    // DASH-2728 (AC8): echo the confirmed orientation so the BE rebuilds the SAME table deterministically
    // rather than re-running the AI structure pass on commit. Only appended when set — a BE predating the
    // commit-side binding (DASH-2727) ignores them and re-detects, exactly as the legacy path did.
    if (payload.transposed != null) formData.append('transposed', payload.transposed.toString());
    if (payload.dataStartRowIndex != null) formData.append('dataStartRowIndex', payload.dataStartRowIndex.toString());
    // DASH-3159/3157: echo the applied REAL-header-row index. This is what triggers the BE's deterministic
    // applied-structure path (LabImportCommitRequestModel.HeaderRowIndex): only when it is present does the BE
    // rebuild from the echoed transposed/dataStartRowIndex/footerRowIndices with no AI structure call — so the
    // committed table is byte-identical to the previewed one. Omitted on the legacy override path (BE re-detects)
    // and a BE predating the change ignores it.
    if (payload.headerRowIndex != null) formData.append('headerRowIndex', payload.headerRowIndex.toString());
    // DASH-3159: echo the applied footer-row set (JSON array) so the whole applied structure round-trips and
    // the BE reproduces the exact excluded-row set from the preview. Only appended when supplied — a BE
    // predating the change ignores it and re-detects, exactly as before.
    if (payload.footerRowIndices != null) formData.append('footerRowIndices', JSON.stringify(payload.footerRowIndices));
    // DASH-2720: attach the optional supplemental document (typically PDF). Bound by the BE (DASH-2719) as
    // LabImportCommitRequestModel.SupplementalFile ([FromForm] is case-insensitive). Only appended when the
    // user picked one — a results-only commit sends no part and a BE predating the change ignores it.
    if (payload.supplementalFile) formData.append('supplementalFile', payload.supplementalFile);
    return this.http.post<LabResultImportResponse>(this.getApiUrl('lab-result-imports/commit'), formData);
  }
}
